import { Component, OnInit, inject } from '@angular/core';
import { Button } from '../../shared/components/button/button';
import { RevealDirective } from '../../shared/directives/reveal.directive';
import { SpotlightDirective } from '../../shared/directives/spotlight.directive';
import { CalendlyService } from '../../shared/services/calendly.service';
import { SeoService } from '../../shared/services/seo.service';

@Component({
  selector: 'app-ai-automation',
  imports: [Button, RevealDirective, SpotlightDirective],
  templateUrl: './ai-automation.html',
  styleUrl: './ai-automation.scss',
})
export class AiAutomation implements OnInit {
  private calendly = inject(CalendlyService);
  private seo = inject(SeoService);

  /** "ai automation services" — pill label + paired description per row. */
  protected services: { label: string; detail: string }[] = [
    {
      label: 'workflow automation',
      detail:
        'We map your existing processes, identify the bottlenecks, and build automated workflows that connect your tools and eliminate manual handoffs.',
    },
    {
      label: 'intelligent document processing',
      detail:
        'Extract, classify, and route information from invoices, contracts, forms, and emails—no more copy-pasting between systems.',
    },
    {
      label: 'ai-powered customer support',
      detail:
        'Deploy smart assistants that handle routine inquiries instantly, qualify leads, and escalate to your team only when human judgment is needed.',
    },
    {
      label: 'data & reporting automation',
      detail:
        'Turn scattered data into automated dashboards and reports that update in real time, giving leadership the visibility to make faster decisions.',
    },
    {
      label: 'custom ai integrations',
      detail:
        'Connect AI capabilities directly into the platforms you already use—CRM, ERP, marketing, and internal tools—without ripping out your stack.',
    },
  ];

  /** "how it works" — numbered process steps, rendered as a cascading glass staircase. */
  protected steps: { term: string; detail: string }[] = [
    { term: 'Discover', detail: 'We assess your processes and pinpoint the highest-impact automation opportunities.' },
    { term: 'Design', detail: 'We architect a solution tailored to your tools, team, and goals.' },
    { term: 'Deploy', detail: 'We build, test, and launch your automations with minimal disruption.' },
    { term: 'Optimize', detail: 'We monitor performance and refine continuously to maximize results.' },
  ];

  /** "why mid-market companies choose semnexus" — gradient pill + paired reason. */
  protected reasons: { label: string; detail: string }[] = [
    {
      label: 'built for your scale',
      detail:
        'We understand the unique pressures of mid-market operations—lean teams, ambitious targets, and no room for bloated enterprise overhead.',
    },
    {
      label: 'measurable roi',
      detail:
        'Every automation we build is tied to a clear outcome: hours saved, errors reduced, or revenue gained.',
    },
    {
      label: 'no disruption',
      detail:
        'We integrate with your existing systems and roll out in phases, so your operations never skip a beat.',
    },
    {
      label: 'a partner, not just a vendor',
      detail:
        'We stay involved after launch to optimize, expand, and ensure your automations keep delivering.',
    },
  ];

  ngOnInit() {
    this.seo.apply({
      title: 'AI Automation Agency',
      description:
        'SemNexus is an AI automation agency that helps mid-market companies automate repetitive, time-draining processes — so your team can focus on the work that actually moves the business forward.',
      canonical: '/ai-automation-agency/',
      image: 'https://semnexus.com/images/bg-header.png',
      imageAlt: 'AI automation agency by SemNexus',
      type: 'website',
    });
    this.seo.setBreadcrumbs([
      { name: 'Home', url: '/' },
      { name: 'AI Automation' },
    ]);
    this.seo.setJsonLd('service', {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': 'https://semnexus.com/ai-automation-agency/#service',
      name: 'AI Automation',
      serviceType: 'Business process automation with AI',
      description:
        'AI automation services for mid-market companies — workflow automation, intelligent document processing, AI-powered customer support, data & reporting automation, and custom AI integrations.',
      provider: { '@id': 'https://semnexus.com/#organization' },
      areaServed: { '@type': 'Country', name: 'United States' },
      url: 'https://semnexus.com/ai-automation-agency/',
      offers: {
        '@type': 'OfferCatalog',
        name: 'AI automation services',
        itemListElement: this.services.map((s) => ({
          '@type': 'Offer',
          name: s.label,
          description: s.detail,
        })),
      },
    });
  }

  scheduleCall() {
    this.calendly.openPopup().catch(() => {});
  }
}
